/**
 * RecoloredHeroSvg — HERO-BG (#93) robust uploaded-SVG recolour layer.
 *
 * Renders an absolute, full-bleed overlay that recolours an uploaded line-art /
 * monochrome SVG hero so the Template-Studio stroke colour and/or gradient fill
 * VISIBLY apply — in the Studio Cover preview, the production scroll viewer, and
 * the headless-Chromium `/print` PDF cover (all share `HeroImageCover`).
 *
 * STRATEGY (CORS-independent, no opaque-box artefact):
 *   1. PRIMARY — fetch the SVG TEXT (same-origin: the hero is served via the
 *      token-gated `/api/storage/logo-file` proxy; `toRelativeLogoMediaUrl`
 *      forces the request origin-relative so it is never cross-origin / mixed-
 *      content), recolour its `stroke`/`fill` markup directly
 *      (`recolorHeroSvgMarkup`), and inline the result. This recolours the real
 *      geometry, so transparent-background line art takes the new colour with
 *      NO solid box, and the gradient case gets a true `<linearGradient>` fill.
 *   2. FALLBACK — while the fetch is in flight, or if it fails (e.g. a genuinely
 *      cross-origin CDN SVG with no CORS), paint a same-origin CSS `mask-image`
 *      over a gradient/stroke-coloured div. This is the prior behaviour and is
 *      correct whenever the mask resource loads.
 *
 * The previous implementation rendered ONLY the CSS mask AND gated on a regex
 * (`/\.svg(\?|#|$)/i`) that never matched the proxy URL — so the layer was never
 * rendered at all and the raw grey SVG showed through. The gate fix lives in
 * `isLikelySvgUrl`; this component makes the recolour itself robust.
 */
import React from 'react';
import { recolorHeroSvgMarkup } from '../../../../lib/recolorHeroSvgMarkup';
import { toRelativeLogoMediaUrl } from '../../../../lib/logoStorageUrl';
import type { HeroCoverSettings } from '../../../../types/reportTemplate';

type HeroImageAlign = NonNullable<HeroCoverSettings['imageAlign']>;

interface Props {
  /** The uploaded SVG hero URL (`heroCover.imageUrl`). */
  imageUrl: string;
  /** Stroke / line colour (hex). */
  strokeColor?: string;
  /** Ordered gradient stops (≥2 hex) for the fill. */
  gradientStops: string[];
  /**
   * Optional 9-position alignment for the UPLOADED SVG. When set and not
   * `center`, the SVG renders `object-fit: contain` + the mapped
   * `object-position` (aspect-preserving, positioned). When unset or `center`,
   * the SVG keeps the byte-stable full-bleed `object-cover` behaviour.
   */
  align?: HeroCoverSettings['imageAlign'];
}

/**
 * Map a 9-position alignment to a CSS `object-position` / `mask-position`
 * value (`<horizontal> <vertical>`), e.g. `bottom-left` → `left bottom`,
 * `top-right` → `right top`, `center-left` → `left center`, `center` →
 * `center`. Used for BOTH the inlined-SVG path (object-position) and the
 * CSS-mask fallback (mask-position).
 */
function alignToObjectPosition(align: HeroImageAlign): string {
  if (align === 'center') return 'center';
  const [v, h] = align.split('-') as [
    'top' | 'center' | 'bottom',
    'left' | 'center' | 'right',
  ];
  const horizontal = h === 'center' ? 'center' : h; // 'left' | 'right' | 'center'
  const vertical = v === 'center' ? 'center' : v; // 'top' | 'bottom' | 'center'
  return `${horizontal} ${vertical}`;
}

/**
 * Map a 9-position alignment to an SVG `preserveAspectRatio` value, e.g.
 * `bottom-center` → `xMidYMax slice`, `top-left` → `xMinYMin slice`. This is the
 * CORRECT lever for positioning INLINE `<svg>` content: `object-fit` /
 * `object-position` apply only to REPLACED elements (img/video/…), NOT to an
 * inlined `<svg>`, so the prior object-position approach was a no-op and a
 * `bottom` alignment still centred the art (leaving a gap at the bottom).
 *
 * FULL-BLEED COVER FIX (William's recurring "cream band") — the meaning of the
 * Y/X anchor is preserved (it still chooses WHICH part of a non-A4-aspect
 * artwork is shown — e.g. `bottom-center` keeps the bottom edge in view), but
 * the FIT switched from `meet` (contain → letterbox bands = the bare "cream
 * band" gap on a full-bleed cover) to `slice` (cover → the artwork FILLS the
 * cover box, cropping the overflow). So the cover is now gap-free at every
 * alignment while still honouring the chosen anchor. `center` (and unset) keeps
 * the byte-stable full-bleed `object-cover` path elsewhere — it never reaches
 * this mapper.
 */
function alignToPreserveAspectRatio(align: HeroImageAlign): string {
  if (align === 'center') return 'xMidYMid slice';
  const [v, h] = align.split('-') as [
    'top' | 'center' | 'bottom',
    'left' | 'center' | 'right',
  ];
  const x = h === 'left' ? 'xMin' : h === 'right' ? 'xMax' : 'xMid';
  const y = v === 'top' ? 'YMin' : v === 'bottom' ? 'YMax' : 'YMid';
  return `${x}${y} slice`;
}

/**
 * Set `preserveAspectRatio` on the FIRST `<svg>` opening tag of the inlined
 * markup — replacing an existing attribute or inserting one — so the artwork
 * anchors to the requested edge. Returns the markup unchanged if no `<svg>`
 * tag is found.
 */
function withPreserveAspectRatio(markup: string, par: string): string {
  const open = markup.match(/<svg\b[^>]*>/i);
  if (!open) return markup;
  const tag = open[0];
  const next = /preserveAspectRatio\s*=/i.test(tag)
    ? tag.replace(/preserveAspectRatio\s*=\s*("[^"]*"|'[^']*')/i, `preserveAspectRatio="${par}"`)
    : tag.replace(/<svg\b/i, `<svg preserveAspectRatio="${par}"`);
  return markup.replace(tag, next);
}

/** True when an explicit, non-`center` alignment was requested. */
function isAligned(align: Props['align']): align is HeroImageAlign {
  return Boolean(align) && align !== 'center';
}

/** CSS mask styles used by the loading / fetch-failure fallback. */
function fallbackMaskStyle(
  maskUrl: string,
  strokeColor: string | undefined,
  gradientStops: string[],
  align: Props['align'],
): React.CSSProperties {
  // FULL-BLEED COVER FIX — the mask now uses `mask-size: cover` at EVERY
  // alignment (was `contain` when aligned, which letterboxed → the bare "cream
  // band" gap on the full-bleed cover). `cover` fills the box; the chosen
  // `mask-position` still selects WHICH part of the art is shown. Mirrors the
  // inline path's `slice` semantics so the loading/fetch-failure fallback is
  // gap-free too.
  const aligned = isAligned(align);
  const position = aligned ? alignToObjectPosition(align) : 'center';
  const size = 'cover';
  return {
    WebkitMaskImage: `url(${maskUrl})`,
    maskImage: `url(${maskUrl})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: position,
    maskPosition: position,
    WebkitMaskSize: size,
    maskSize: size,
    background: gradientStops.length >= 2
      ? `linear-gradient(to bottom, ${gradientStops.join(', ')})`
      : (strokeColor ?? 'currentColor'),
  };
}

export const RecoloredHeroSvg: React.FC<Props> = ({ imageUrl, strokeColor, gradientStops, align }) => {
  // Make the fetch / mask URL origin-relative when it is the hosted logo-media
  // proxy, so neither the fetch nor the CSS mask is cross-origin / mixed-content.
  const safeUrl = toRelativeLogoMediaUrl(imageUrl) || imageUrl;
  const [markup, setMarkup] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setMarkup(null);
    setFailed(false);
    // `data:` SVGs can be inlined without a network round-trip.
    if (/^data:image\/svg\+xml/i.test(safeUrl)) {
      try {
        const comma = safeUrl.indexOf(',');
        const payload = safeUrl.slice(comma + 1);
        const text = /;base64/i.test(safeUrl.slice(0, comma))
          ? (typeof atob === 'function' ? atob(payload) : payload)
          : decodeURIComponent(payload);
        if (text.includes('<svg')) {
          setMarkup(recolorHeroSvgMarkup(text, { strokeColor, gradientStops }));
        } else {
          setFailed(true);
        }
      } catch {
        setFailed(true);
      }
      return () => { cancelled = true; };
    }
    if (typeof fetch !== 'function') {
      setFailed(true);
      return () => { cancelled = true; };
    }
    fetch(safeUrl, { credentials: 'same-origin' })
      .then(res => (res.ok ? res.text() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then(text => {
        if (cancelled) return;
        if (!text.includes('<svg')) { setFailed(true); return; }
        setMarkup(recolorHeroSvgMarkup(text, { strokeColor, gradientStops }));
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [safeUrl, strokeColor, gradientStops]);

  // PRIMARY — inlined, markup-recoloured SVG. `dangerouslySetInnerHTML` is the
  // only way to inline external SVG markup; the source is the author's OWN
  // uploaded hero (already type/size-gated at upload), served from our token-
  // gated same-origin proxy, so this is not an untrusted-input vector.
  if (markup) {
    // ALIGNMENT — `object-fit`/`object-position` do NOT apply to an inlined
    // `<svg>` (only to replaced elements), so a `bottom` alignment used to
    // still centre the art and leave a gap. The correct lever is the SVG's
    // own `preserveAspectRatio`: inject the mapped value (e.g. bottom-center →
    // `xMidYMax slice`) onto the inlined markup so the artwork anchors to the
    // requested edge AND FILLS the cover box (`slice` = cover semantics, no
    // letterbox "cream band"; the anchor still chooses which part is shown).
    // The SVG fills the box (`h-full w-full`); the unset/`center` path keeps
    // the byte-stable full-bleed `object-cover` default.
    const aligned = isAligned(align);
    const displayMarkup = aligned
      ? withPreserveAspectRatio(markup, alignToPreserveAspectRatio(align))
      : markup;
    const svgFitClass = aligned ? '' : '[&>svg]:object-cover';
    return (
      <div
        className={`absolute inset-0 pointer-events-none [&>svg]:h-full [&>svg]:w-full ${svgFitClass}`.trim()}
        data-testid="cover-hero-recolored-svg"
        data-hero-recolor-mode="inline"
        data-hero-align={aligned ? align : undefined}
        data-hero-preserve-aspect-ratio={aligned ? alignToPreserveAspectRatio(align) : undefined}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: displayMarkup }}
      />
    );
  }

  // FALLBACK — same-origin CSS mask while loading / on fetch failure.
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      data-testid="cover-hero-recolored-svg"
      data-hero-recolor-mode={failed ? 'mask-fallback' : 'mask-loading'}
      data-hero-align={isAligned(align) ? align : undefined}
      style={fallbackMaskStyle(safeUrl, strokeColor, gradientStops, align)}
    />
  );
};
